/* eslint-disable react/no-unknown-property */
import { useRef, useState, useEffect, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import { Html } from '@react-three/drei';
import * as THREE from 'three';

// Consultant States
export const CONSULTANT_STATES = {
  IDLE: 'idle',
  LOOKING_AT_USER: 'looking_at_user',
  WALKING_TO_VANITY: 'walking_to_vanity',
  SITTING: 'sitting',
  TYPING: 'typing',
  LOOKING_BACK: 'looking_back',
  ORGANIZING: 'organizing'
};

// Stylized Female Consultant Character (Procedural) - Enhanced with Natural Micro-animations
const BeautyConsultant = ({ 
  state = CONSULTANT_STATES.IDLE,
  position = [-1.2, 0, -1.5],
  onStateChange,
  showWelcome = false
}) => {
  const groupRef = useRef();
  const headRef = useRef();
  const bodyRef = useRef();
  const leftArmRef = useRef();
  const rightArmRef = useRef();
  const [isBlinking, setIsBlinking] = useState(false);
  const [currentPosition, setCurrentPosition] = useState(position);
  const targetPositionRef = useRef(new THREE.Vector3(...position));
  
  // Animation timers and state
  const animationTime = useRef(0);
  const idleVariation = useRef(Math.random() * Math.PI * 2);
  const blinkTimer = useRef(0);
  const smileAmount = useRef(0);
  const lastSmileTime = useRef(0);

  // State positions
  const positions = useMemo(() => ({
    idle: [-1.2, 0, -1.5],
    vanity: [0, 0, -1.6],
    sitting: [0, 0.4, -1.6]
  }), []);

  // Handle state changes and position updates
  useEffect(() => {
    switch (state) {
      case CONSULTANT_STATES.WALKING_TO_VANITY:
      case CONSULTANT_STATES.SITTING:
      case CONSULTANT_STATES.TYPING:
        targetPositionRef.current.set(...positions.vanity);
        break;
      case CONSULTANT_STATES.IDLE:
      case CONSULTANT_STATES.ORGANIZING:
      case CONSULTANT_STATES.LOOKING_AT_USER:
      default:
        targetPositionRef.current.set(...positions.idle);
        break;
    }
  }, [state, positions]);

  // Main animation loop with enhanced micro-animations
  useFrame((frameState, delta) => {
    animationTime.current += delta;
    const time = animationTime.current;

    // Natural breathing animation
    const breathRate = 1.2; // breaths per second
    const breathDepth = 0.008;
    const breathOffset = Math.sin(time * breathRate * Math.PI * 2) * breathDepth;

    // Blink timer - variable intervals for natural feel
    blinkTimer.current += delta;
    const blinkInterval = 2.5 + Math.sin(time * 0.1) * 1.5; // 1-4 seconds
    if (blinkTimer.current > blinkInterval && !isBlinking) {
      setIsBlinking(true);
      blinkTimer.current = 0;
      // Double blink occasionally
      const doubleBlink = Math.random() > 0.85;
      setTimeout(() => setIsBlinking(false), 100);
      if (doubleBlink) {
        setTimeout(() => setIsBlinking(true), 200);
        setTimeout(() => setIsBlinking(false), 300);
      }
    }

    // Occasional smile micro-animation
    if (time - lastSmileTime.current > 5 + Math.random() * 10) {
      lastSmileTime.current = time;
    }
    const timeSinceSmile = time - lastSmileTime.current;
    if (timeSinceSmile < 2) {
      smileAmount.current = Math.sin(timeSinceSmile * Math.PI / 2) * 0.5;
    } else {
      smileAmount.current *= 0.95;
    }

    // Smooth position interpolation (walking)
    const currentPos = new THREE.Vector3(...currentPosition);
    const distance = currentPos.distanceTo(targetPositionRef.current);
    if (distance > 0.02) {
      currentPos.lerp(targetPositionRef.current, delta * 2.5);
      setCurrentPosition([currentPos.x, currentPos.y, currentPos.z]);
    }

    // Enhanced head movement based on state
    if (headRef.current) {
      // Base subtle idle movement
      const idleHeadX = Math.sin(time * 0.15 + idleVariation.current) * 0.03;
      const idleHeadY = Math.sin(time * 0.2) * 0.08;
      
      switch (state) {
        case CONSULTANT_STATES.LOOKING_AT_USER:
          // Look toward camera/user with subtle movement
          headRef.current.rotation.y = THREE.MathUtils.lerp(
            headRef.current.rotation.y,
            0.3 + Math.sin(time * 0.5) * 0.05,
            delta * 3
          );
          headRef.current.rotation.x = THREE.MathUtils.lerp(
            headRef.current.rotation.x,
            idleHeadX,
            delta * 2
          );
          break;
        case CONSULTANT_STATES.TYPING:
          // Look down at laptop with slight variation
          headRef.current.rotation.x = THREE.MathUtils.lerp(
            headRef.current.rotation.x,
            0.35 + Math.sin(time * 1.5) * 0.02,
            delta * 2
          );
          headRef.current.rotation.y = THREE.MathUtils.lerp(
            headRef.current.rotation.y,
            Math.sin(time * 0.8) * 0.03,
            delta * 2
          );
          break;
        case CONSULTANT_STATES.LOOKING_BACK:
          // Look back at user with gentle turn
          headRef.current.rotation.x = THREE.MathUtils.lerp(
            headRef.current.rotation.x,
            0.05,
            delta * 3
          );
          headRef.current.rotation.y = THREE.MathUtils.lerp(
            headRef.current.rotation.y,
            0.4 + Math.sin(time * 0.6) * 0.03,
            delta * 3
          );
          break;
        case CONSULTANT_STATES.ORGANIZING:
          // Look around at products naturally
          headRef.current.rotation.y = Math.sin(time * 0.4) * 0.35;
          headRef.current.rotation.x = Math.sin(time * 0.25) * 0.12;
          break;
        default:
          // Idle - natural looking around
          headRef.current.rotation.y = THREE.MathUtils.lerp(
            headRef.current.rotation.y,
            idleHeadY,
            delta * 1.5
          );
          headRef.current.rotation.x = THREE.MathUtils.lerp(
            headRef.current.rotation.x,
            idleHeadX,
            delta * 1.5
          );
      }
    }

    // Enhanced arm animations
    if (leftArmRef.current && rightArmRef.current) {
      // Base idle sway
      const armSway = Math.sin(time * 0.4) * 0.02;
      
      switch (state) {
        case CONSULTANT_STATES.TYPING:
          // Typing animation - more natural keyboard movement
          const typeOffset = time * 8;
          leftArmRef.current.rotation.x = -1.15 + Math.sin(typeOffset) * 0.08;
          rightArmRef.current.rotation.x = -1.15 + Math.sin(typeOffset + Math.PI * 0.7) * 0.08;
          leftArmRef.current.rotation.z = 0.25;
          rightArmRef.current.rotation.z = -0.25;
          // Subtle wrist movement
          leftArmRef.current.rotation.y = Math.sin(typeOffset * 0.5) * 0.05;
          rightArmRef.current.rotation.y = -Math.sin(typeOffset * 0.5) * 0.05;
          break;
        case CONSULTANT_STATES.ORGANIZING:
          // Organizing products animation
          leftArmRef.current.rotation.x = -0.5 + Math.sin(time * 1.5) * 0.25;
          rightArmRef.current.rotation.x = -0.3 + Math.sin(time * 1.2 + 1) * 0.2;
          leftArmRef.current.rotation.z = 0.2 + Math.sin(time * 0.8) * 0.08;
          rightArmRef.current.rotation.z = -0.2;
          break;
        default:
          // Idle arm position with subtle movement
          leftArmRef.current.rotation.x = THREE.MathUtils.lerp(
            leftArmRef.current.rotation.x,
            0.08 + Math.sin(time * 0.35) * 0.03 + armSway,
            delta * 2
          );
          rightArmRef.current.rotation.x = THREE.MathUtils.lerp(
            rightArmRef.current.rotation.x,
            0.08 + Math.sin(time * 0.35 + 0.5) * 0.03 + armSway,
            delta * 2
          );
          leftArmRef.current.rotation.z = 0.12;
          rightArmRef.current.rotation.z = -0.12;
      }
    }

    // Body movement with breathing
    if (bodyRef.current) {
      // Breathing effect
      bodyRef.current.position.y = breathOffset;
      bodyRef.current.scale.x = 1 + breathOffset * 0.5;
      bodyRef.current.scale.z = 1 + breathOffset * 0.3;
      
      // Subtle body sway
      if (state === CONSULTANT_STATES.IDLE || state === CONSULTANT_STATES.ORGANIZING) {
        bodyRef.current.rotation.y = Math.sin(time * 0.25) * 0.015;
        bodyRef.current.rotation.z = Math.sin(time * 0.2) * 0.008;
      }
    }
  });

  // Skin and clothing colors
  const skinColor = "#F5D0C5";
  const hairColor = "#3D2314";
  const dressColor = "#BA7B7C";
  const accentColor = "#D4A574";

  // Calculate smile state
  const isSmiling = state === CONSULTANT_STATES.LOOKING_AT_USER || 
                    state === CONSULTANT_STATES.LOOKING_BACK ||
                    smileAmount.current > 0.1;

  return (
    <group ref={groupRef} position={currentPosition}>
      {/* Welcome message when user approaches */}
      {showWelcome && (
        <Html position={[0, 2.2, 0]} center distanceFactor={8}>
          <div 
            className="animate-fade-in"
            style={{ 
              minWidth: '280px',
              background: 'linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(253,248,246,0.98) 100%)',
              borderRadius: '20px',
              padding: '20px 24px',
              boxShadow: '0 20px 50px rgba(186,123,124,0.25), 0 8px 24px rgba(0,0,0,0.1)',
              border: '1px solid rgba(186,123,124,0.15)',
              textAlign: 'center'
            }}
          >
            <p className="text-lg text-[#BA7B7C] mb-2" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: '600' }}>
              ✨ Bienvenida a AliGlam Beauty Studio
            </p>
            <p className="text-sm text-[#57534E]">Presiona E para hablar con tu Beauty Advisor</p>
          </div>
        </Html>
      )}

      {/* Body Group */}
      <group ref={bodyRef}>
        {/* Torso/Dress */}
        <mesh position={[0, 1.1, 0]} castShadow>
          <cylinderGeometry args={[0.15, 0.2, 0.5, 16]} />
          <meshStandardMaterial color={dressColor} roughness={0.6} />
        </mesh>
        
        {/* Dress bottom */}
        <mesh position={[0, 0.7, 0]} castShadow>
          <cylinderGeometry args={[0.2, 0.25, 0.4, 16]} />
          <meshStandardMaterial color={dressColor} roughness={0.6} />
        </mesh>
        
        {/* Legs */}
        <mesh position={[-0.08, 0.35, 0]} castShadow>
          <cylinderGeometry args={[0.05, 0.04, 0.5, 8]} />
          <meshStandardMaterial color={skinColor} roughness={0.8} />
        </mesh>
        <mesh position={[0.08, 0.35, 0]} castShadow>
          <cylinderGeometry args={[0.05, 0.04, 0.5, 8]} />
          <meshStandardMaterial color={skinColor} roughness={0.8} />
        </mesh>
        
        {/* Shoes */}
        <mesh position={[-0.08, 0.08, 0.02]} castShadow>
          <boxGeometry args={[0.06, 0.04, 0.12]} />
          <meshStandardMaterial color={accentColor} roughness={0.4} metalness={0.2} />
        </mesh>
        <mesh position={[0.08, 0.08, 0.02]} castShadow>
          <boxGeometry args={[0.06, 0.04, 0.12]} />
          <meshStandardMaterial color={accentColor} roughness={0.4} metalness={0.2} />
        </mesh>

        {/* Neck */}
        <mesh position={[0, 1.4, 0]} castShadow>
          <cylinderGeometry args={[0.05, 0.06, 0.1, 8]} />
          <meshStandardMaterial color={skinColor} roughness={0.8} />
        </mesh>

        {/* Arms */}
        <group ref={leftArmRef} position={[-0.2, 1.25, 0]}>
          <mesh position={[0, -0.12, 0]} castShadow>
            <cylinderGeometry args={[0.035, 0.04, 0.25, 8]} />
            <meshStandardMaterial color={skinColor} roughness={0.8} />
          </mesh>
          <mesh position={[0, -0.32, 0.05]} rotation={[0.3, 0, 0]} castShadow>
            <cylinderGeometry args={[0.03, 0.035, 0.2, 8]} />
            <meshStandardMaterial color={skinColor} roughness={0.8} />
          </mesh>
          <mesh position={[0, -0.45, 0.1]} castShadow>
            <sphereGeometry args={[0.035, 8, 8]} />
            <meshStandardMaterial color={skinColor} roughness={0.8} />
          </mesh>
        </group>

        <group ref={rightArmRef} position={[0.2, 1.25, 0]}>
          <mesh position={[0, -0.12, 0]} castShadow>
            <cylinderGeometry args={[0.035, 0.04, 0.25, 8]} />
            <meshStandardMaterial color={skinColor} roughness={0.8} />
          </mesh>
          <mesh position={[0, -0.32, 0.05]} rotation={[0.3, 0, 0]} castShadow>
            <cylinderGeometry args={[0.03, 0.035, 0.2, 8]} />
            <meshStandardMaterial color={skinColor} roughness={0.8} />
          </mesh>
          <mesh position={[0, -0.45, 0.1]} castShadow>
            <sphereGeometry args={[0.035, 8, 8]} />
            <meshStandardMaterial color={skinColor} roughness={0.8} />
          </mesh>
        </group>

        {/* Head Group */}
        <group ref={headRef} position={[0, 1.55, 0]}>
          {/* Head */}
          <mesh castShadow>
            <sphereGeometry args={[0.12, 16, 16]} />
            <meshStandardMaterial color={skinColor} roughness={0.8} />
          </mesh>
          
          {/* Hair - back */}
          <mesh position={[0, 0.02, -0.02]} castShadow>
            <sphereGeometry args={[0.13, 16, 16, 0, Math.PI * 2, 0, Math.PI / 2]} />
            <meshStandardMaterial color={hairColor} roughness={0.9} />
          </mesh>
          
          {/* Hair - bangs */}
          <mesh position={[0, 0.08, 0.06]} castShadow>
            <boxGeometry args={[0.2, 0.06, 0.08]} />
            <meshStandardMaterial color={hairColor} roughness={0.9} />
          </mesh>
          
          {/* Hair - sides */}
          <mesh position={[-0.1, -0.02, 0]} castShadow>
            <cylinderGeometry args={[0.03, 0.02, 0.25, 8]} />
            <meshStandardMaterial color={hairColor} roughness={0.9} />
          </mesh>
          <mesh position={[0.1, -0.02, 0]} castShadow>
            <cylinderGeometry args={[0.03, 0.02, 0.25, 8]} />
            <meshStandardMaterial color={hairColor} roughness={0.9} />
          </mesh>
          
          {/* Eyes - whites */}
          <mesh position={[-0.04, 0.02, 0.1]}>
            <sphereGeometry args={[0.02, 8, 8]} />
            <meshStandardMaterial color="#FFFFFF" roughness={0.3} />
          </mesh>
          <mesh position={[0.04, 0.02, 0.1]}>
            <sphereGeometry args={[0.02, 8, 8]} />
            <meshStandardMaterial color="#FFFFFF" roughness={0.3} />
          </mesh>
          
          {/* Pupils */}
          <mesh position={[-0.04, 0.02, 0.115]}>
            <sphereGeometry args={[0.01, 8, 8]} />
            <meshStandardMaterial color="#3D2314" roughness={0.5} />
          </mesh>
          <mesh position={[0.04, 0.02, 0.115]}>
            <sphereGeometry args={[0.01, 8, 8]} />
            <meshStandardMaterial color="#3D2314" roughness={0.5} />
          </mesh>
          
          {/* Eyelids (for blinking) */}
          {isBlinking && (
            <>
              <mesh position={[-0.04, 0.025, 0.11]}>
                <boxGeometry args={[0.05, 0.025, 0.02]} />
                <meshStandardMaterial color={skinColor} />
              </mesh>
              <mesh position={[0.04, 0.025, 0.11]}>
                <boxGeometry args={[0.05, 0.025, 0.02]} />
                <meshStandardMaterial color={skinColor} />
              </mesh>
            </>
          )}
          
          {/* Eyebrows - slightly raised when looking */}
          <mesh position={[-0.04, 0.06, 0.1]} rotation={[0, 0, 0.1]}>
            <boxGeometry args={[0.04, 0.008, 0.01]} />
            <meshStandardMaterial color={hairColor} />
          </mesh>
          <mesh position={[0.04, 0.06, 0.1]} rotation={[0, 0, -0.1]}>
            <boxGeometry args={[0.04, 0.008, 0.01]} />
            <meshStandardMaterial color={hairColor} />
          </mesh>
          
          {/* Nose */}
          <mesh position={[0, -0.01, 0.11]}>
            <coneGeometry args={[0.015, 0.03, 8]} />
            <meshStandardMaterial color={skinColor} roughness={0.8} />
          </mesh>
          
          {/* Lips/Mouth */}
          <mesh position={[0, -0.05, 0.1]}>
            <boxGeometry args={[0.04, 0.015, 0.01]} />
            <meshStandardMaterial color="#E8A0A0" roughness={0.5} />
          </mesh>
          
          {/* Smile curves (when smiling) */}
          {isSmiling && (
            <>
              <mesh position={[-0.025, -0.055, 0.1]} rotation={[0, 0, 0.3]}>
                <boxGeometry args={[0.015, 0.005, 0.005]} />
                <meshStandardMaterial color="#D08080" />
              </mesh>
              <mesh position={[0.025, -0.055, 0.1]} rotation={[0, 0, -0.3]}>
                <boxGeometry args={[0.015, 0.005, 0.005]} />
                <meshStandardMaterial color="#D08080" />
              </mesh>
            </>
          )}
          
          {/* Earrings */}
          <mesh position={[-0.12, 0, 0]}>
            <sphereGeometry args={[0.012, 8, 8]} />
            <meshStandardMaterial color={accentColor} roughness={0.3} metalness={0.6} />
          </mesh>
          <mesh position={[0.12, 0, 0]}>
            <sphereGeometry args={[0.012, 8, 8]} />
            <meshStandardMaterial color={accentColor} roughness={0.3} metalness={0.6} />
          </mesh>
        </group>

        {/* Necklace */}
        <mesh position={[0, 1.32, 0.08]}>
          <torusGeometry args={[0.08, 0.008, 8, 32, Math.PI]} />
          <meshStandardMaterial color={accentColor} roughness={0.3} metalness={0.6} />
        </mesh>
        <mesh position={[0, 1.25, 0.1]}>
          <sphereGeometry args={[0.015, 8, 8]} />
          <meshStandardMaterial color="#FFD4E5" roughness={0.3} metalness={0.3} />
        </mesh>
      </group>
    </group>
  );
};

export default BeautyConsultant;
